// Workstream B #34 — build a `QualifiedName` from Object-Explorer tree parts,
// folding each component per the connection engine.
//
// The OE-tree context menus historically hardcoded `.toLowerCase()` for
// `foldedName` / `foldedSchema` / `foldedDatabase` when constructing the
// rename / find-references request identifier. That is wrong for Oracle
// (unquoted identifiers fold to UPPER): the engine-aware relation-identity
// comparator (`matchRelationRef`, #34) then never matched the walker's
// correctly-folded relation refs, so Oracle rename-table / find-references
// silently resolved to zero references. This mirrors the F42 fix already
// applied to the rename *to*-identifier in `renamePreviewStore.submitNewName`.
//
// Folding is delegated to the resolver's `foldIdentifier`, the single source of
// per-engine identifier-case truth (oracle → upper, postgres → lower,
// mssql/mysql/sqlite → lower; quoted preserves case).

import type { DatabaseEngineId } from '@/core/engines';

import type { SqlEngine } from '../sql-grammar/model';
import { databaseEngineIdToSqlEngine } from '../intellisense/versionGating/engineIdMapping';
import {
  ENGINE_DEFAULT_QUOTING,
  needsQuoting,
  quoteWithStyle,
} from '../sql-resolver/ddl/apply/dialect.quoting';
import { foldIdentifier } from '../sql-resolver/scope/identifiers';

import type { QualifiedName } from './types';

export type OeIdentifierParts = {
  readonly name: string;
  readonly schema?: string;
  readonly database?: string;
  readonly quoted?: boolean;
};

/** Infer whether an Object-Explorer catalog identifier component was defined
 *  with a QUOTED (case-exact) identifier — VALID ONLY for oracle and postgres.
 *
 *  Oracle (unquoted → UPPER) and PostgreSQL (unquoted → lower) fold every
 *  unquoted identifier to a FIXED case, so a stored catalog component whose case
 *  DIFFERS from that fold could only have been produced by a quoted, case-exact
 *  definition — and the identifier index preserves such refs case-exact, so we
 *  must NOT re-fold it away (CK-B P1-2: a PG `"CamelCase"` / quoted Oracle
 *  mixed-case relation lost its case here and no longer matched the index's
 *  quoted refs → find-references/rename silently skipped every dependent body).
 *
 *  For mysql / mssql / sqlite this inference is UNSOUND and is deliberately
 *  disabled (returns false = "not inferred quoted"). These engines store the
 *  DEFINED case regardless of quoting — MySQL case-sensitivity is governed by
 *  the server variable `lower_case_table_names`, not by delimiters — so a mixed-
 *  case stored name is NOT evidence of quoting. Inferring "quoted" there
 *  preserved source case (`Orders`) while the walker/indexer blanket-lowercase
 *  the body ref (`orders`), desyncing producer and walker folds; on a
 *  case-sensitive MySQL server (`lower_case_table_names=0`) the comparator then
 *  missed and OE find-references/rename silently returned ZERO matches (WS-B fix
 *  round 2). A caller that genuinely KNOWS an identifier was quoted still passes
 *  `parts.quoted` explicitly (see {@link buildOeRelationIdentifier}). */
export function inferOeComponentQuoting(engine: SqlEngine, value: string): boolean {
  if (engine !== 'oracle' && engine !== 'postgres') return false;
  return foldIdentifier(value, false, { engine }) !== value;
}

/** Fold a single OE catalog identifier component the way the identifier index
 *  folded the matching body references — unquoted names per engine default,
 *  quoted (case-exact) names verbatim. Quoting is inferred from the stored case
 *  for oracle/postgres ONLY; for mysql/mssql/sqlite this returns the blanket
 *  per-engine-default fold (never infers quoting from a case difference) —
 *  matching exactly what the walker/indexer emit for those engines (see
 *  {@link inferOeComponentQuoting}). This is the fold the engine-aware
 *  relation-identity comparator (`matchRelationRef` / `relationIdentitiesEqual`)
 *  needs so an OE-derived identity actually matches the walker's recorded refs. */
export function foldOeComponent(engine: SqlEngine, value: string): string {
  return foldIdentifier(value, inferOeComponentQuoting(engine, value), { engine });
}

/** A relation as a REFERENCE the engine reads back as this very catalog object
 *  — the text a DDL statement needs, because `dialect.ts`'s `tableText` returns
 *  a `QualifiedName`'s `raw` verbatim and the column ALTER is built on it
 *  (`rename/renameSchemaAlter.ts`).
 *
 *  A bare `database.schema.name` join of the Object-Explorer components misnames
 *  it twice:
 *
 *   • It drops the delimiters a case-EXACT catalog spelling needs. `Users` can
 *     only have been created quoted on Oracle / PostgreSQL (the two engines that
 *     fold an unquoted identifier — {@link inferOeComponentQuoting}), so a bare
 *     `HR.Users` re-reads as `HR.USERS` there (ORA-00942) and `hr.users` on
 *     PostgreSQL. Same for a catalog name that cannot stand bare at all.
 *   • It carries the DATABASE component, which no engine accepts in an
 *     `ALTER TABLE` host but SQL Server — and on Oracle and MySQL the OE
 *     "database" node IS the owner/schema (`go-to/oeTreeQualification.ts`), so
 *     the join repeated it (`HR.HR.Users`). Which database a statement runs
 *     against is decided by the `executeQueryV2({ database })` routing the batch
 *     already does, never by the statement text; `qualified.database` still
 *     carries it for that.
 *
 *  Quoting only where it is REQUIRED keeps the emitted SQL identical on the
 *  three engines whose stored case is not evidence of quoting (mssql / mysql /
 *  sqlite: `inferOeComponentQuoting` is false there by construction).
 *
 *  `quoted` overrides the per-component inference for a caller that KNOWS how
 *  the identifier was written — the same override {@link buildOeRelationIdentifier}
 *  applies to the folded halves, so `raw` and the identity cannot disagree.
 *
 *  Lives here, next to the OE component-spelling helpers, because BOTH rename
 *  entry points build their host relation from Object-Explorer components: the
 *  cursor one (`go-to/columnAtCursor.ts`) and the Object Explorer's own
 *  (`buildOeRelationIdentifier`). One renderer is what keeps them from
 *  drifting. */
export function catalogRelationRef(
  engine: SqlEngine,
  schema: string | undefined,
  name: string,
  quoted?: boolean,
): string {
  const ref = (part: string): string =>
    (quoted ?? inferOeComponentQuoting(engine, part)) || needsQuoting(part, engine)
      ? quoteWithStyle(part, ENGINE_DEFAULT_QUOTING[engine])
      : part;
  return schema ? `${ref(schema)}.${ref(name)}` : ref(name);
}

export function buildOeRelationIdentifier(
  engineId: DatabaseEngineId,
  parts: OeIdentifierParts,
): QualifiedName {
  const engine = databaseEngineIdToSqlEngine(engineId);
  // Per-component fold. When the caller doesn't assert a quoting mode (every OE
  // context-menu caller passes a bare catalog name/schema, never `quoted`),
  // infer it from the catalog case so a quoted mixed-case relation keeps its
  // exact case and an unquoted name folds per engine — instead of the old
  // blanket lowercase that silently missed both (CK-B P1-2).
  const foldPart = (value: string): string => {
    if (!engine) return value.toLowerCase();
    const quoted = parts.quoted ?? inferOeComponentQuoting(engine, value);
    return foldIdentifier(value, quoted, { engine });
  };
  const nameQuoted = engine
    ? (parts.quoted ?? inferOeComponentQuoting(engine, parts.name))
    : (parts.quoted ?? false);
  return {
    name: parts.name,
    schema: parts.schema,
    database: parts.database,
    // A REFERENCE, not a display join — `dialect.ts`'s `tableText` emits this
    // verbatim into an `ALTER TABLE` host position, so a quoted mixed-case
    // relation has to keep the delimiters `quoted` (just above) already says it
    // has. The engine-less fallback keeps the historical bare join, which is
    // all a non-SQL engine (Kusto) can be given anyway.
    raw: engine
      ? catalogRelationRef(engine, parts.schema, parts.name, parts.quoted)
      : [parts.schema, parts.name].filter(Boolean).join('.') || parts.name,
    quoted: nameQuoted,
    foldedName: foldPart(parts.name),
    foldedSchema: parts.schema ? foldPart(parts.schema) : undefined,
    foldedDatabase: parts.database ? foldPart(parts.database) : undefined,
  };
}
